'use client'

import { STRUCTURAL_REFETCH_INTERVAL, STRUCTURAL_STALE_TIME } from '@/config/consts'
import { publicEulerRoutes } from '@/lib/euler-api/publicRouteSpecs'
import { useQuery } from '@tanstack/react-query'
import { Address, getAddress } from 'viem'

export interface NativeApy {
  asset: Address
  timestamp: number
  apy: number
  provider: string
  source: string
  description?: string | null
  apyDuration?: string
  apyDurationSeconds?: number
}

export type NativeApysResponse = Record<Address, NativeApy>

interface UseNativeApysParams {
  chainId: number
  enabled?: boolean
}

export function useNativeApys({ chainId, enabled = true }: UseNativeApysParams) {
  return useQuery<NativeApysResponse>({
    queryKey: ['nativeApys', chainId],
    queryFn: async () => {
      try {
        const response = await fetch(publicEulerRoutes.intrinsicApyLatest.clientUrl(chainId))
        if (!response.ok) {
          console.error('Failed to fetch native APYs:', response.statusText)
          return {}
        }
        return response.json()
      } catch (error) {
        console.error('Error fetching native APYs:', error)
        return {}
      }
    },
    staleTime: STRUCTURAL_STALE_TIME,
    refetchInterval: STRUCTURAL_REFETCH_INTERVAL,
    enabled,
  })
}

/**
 * Get native APY for a specific asset
 */
export function getNativeApyForAsset(
  nativeApysData: NativeApysResponse | undefined,
  assetAddress: Address | undefined,
): NativeApy | undefined {
  if (!nativeApysData || !assetAddress) return undefined

  try {
    const checksumAddress = getAddress(assetAddress)
    return nativeApysData[checksumAddress]
  } catch {
    return undefined
  }
}
